/**
 * "Print" on a Premium grid's settings ⋮ (H233) — the ONE print path for DataGridPremium.
 *
 * MUI X 7.1.1 (useGridPrintExport.js, handlePrintWindowLoad) removes the footer from its
 * clone of the grid when `hideFooter` is set, then sets `style` on that removed footer: a
 * TypeError in the print window's load, before anything prints, and the after-print restore
 * never runs (the grid stays in its print state). A grid rendered with no footer at all (the
 * `hideFooter` prop) fails the same way. MUI guards the option in 7.8.0 and the prop in
 * 7.28.0 ("[DataGrid] Fix `printOptions` not respecting `hideFooter` root prop", #16915).
 * EXIT: upgrading @mui/x-data-grid-premium to ≥ 7.28 removes this file — print with
 * MUI's own GridPrintExportMenuItem again.
 *
 * Until then: MUI is never asked to remove the footer (`hideFooter: false`); a requested
 * `hideFooter` hides it in the print window's CSS instead; and a grid with no footer gets a
 * hidden stand-in for the print, removed when MUI removes its print window.
 */
import { MenuItem } from "@mui/material";
import {
  gridClasses,
  useGridApiContext,
  type GridApi,
  type GridPrintExportOptions,
} from "@mui/x-data-grid-premium";
import type { MutableRefObject } from "react";

const HIDE_FOOTER_CSS = `.${gridClasses.footerContainer} { display: none !important; }`;

/** A hidden footer in the grid, for MUI's print to find, until its print window is gone. */
function standInFooter(root: HTMLElement): () => void {
  const shim = document.createElement("div");
  shim.className = gridClasses.footerContainer;
  shim.setAttribute("data-print-footer-stand-in", "");
  shim.style.display = "none";
  root.appendChild(shim);
  const frames = new Set<Node>();
  function remove() {
    observer.disconnect();
    shim.remove();
  }
  const observer = new MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach((n) => n.nodeName === "IFRAME" && frames.add(n));
      r.removedNodes.forEach((n) => frames.has(n) && remove());
    }
  });
  observer.observe(document.body, { childList: true });
  return remove;
}

export async function printGrid(apiRef: MutableRefObject<GridApi>, options: GridPrintExportOptions = {}) {
  const { hideFooter, pageStyle, ...rest } = options;
  const ownStyle = typeof pageStyle === "function" ? pageStyle() : pageStyle ?? "";
  const root = apiRef.current.rootElementRef?.current;
  const removeStandIn =
    root && !root.querySelector(`.${gridClasses.footerContainer}`) ? standInFooter(root) : null;
  try {
    await apiRef.current.exportDataAsPrint({
      ...rest,
      hideFooter: false,
      pageStyle: hideFooter ? `${ownStyle}\n${HIDE_FOOTER_CSS}`.trim() : ownStyle || undefined,
    });
  } catch (error) {
    removeStandIn?.();
    throw error;
  }
}

/** The ⋮'s Print item: MUI's GridPrintExportMenuItem, through `printGrid`. Takes `hideMenu`. */
export function GridPrintMenuItem({
  options,
  hideMenu,
}: {
  options?: GridPrintExportOptions;
  hideMenu?: () => void;
}) {
  const apiRef = useGridApiContext();
  return (
    <MenuItem
      onClick={() => {
        void printGrid(apiRef as unknown as MutableRefObject<GridApi>, options);
        hideMenu?.();
      }}
    >
      {apiRef.current.getLocaleText("toolbarExportPrint")}
    </MenuItem>
  );
}
